import { useFormik } from 'formik'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import trackEvents from '../../constants/track-events'
import { useNotificationContext, usePartnerContext } from '../../context'
import { trackEvent } from '../../helpers/analytics'
import { useMutationAndUpdateQuery } from '../../hooks/use-mutation-and-update-query'
import Divider from '../divider'
import SEO from '../seo'
import Toggle from '../tailwind/toggle'
import SettingsContainer from './settings-container'

const I18N_PREFIX = 'components.settings.dashboard_components.'

const DashboardComponents = () => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()
  const { partner } = usePartnerContext()

  const dashboard_settings = JSON.parse(
    partner.app_settings?.dashboard_settings ?? '{}'
  )

  const { mutate } = useMutationAndUpdateQuery({
    queryKey: QUERY_KEYS.partnerById(partner.id),
    mutation: 'updatePartner'
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // Components are visible by default, only hidden if explicitly set to false
      assessment_monitor_visible:
        dashboard_settings.assessment_monitor_visible !== false,
      cultural_fit_visible: dashboard_settings.cultural_fit_visible !== false,
      matching_visible: dashboard_settings.matching_visible !== false
    },
    onSubmit: (
      { assessment_monitor_visible, cultural_fit_visible, matching_visible },
      { setSubmitting }
    ) => {
      setSubmitting(true)
      trackEvent(trackEvents.UPDATE_DASHBOARD_COMPONENTS_SETTINGS)

      const app_settings = { ...(partner.app_settings || {}) }
      // Bestehende Keys (experimental, table_columns) mitnehmen, sonst löscht
      // dieses Speichern die Einstellungen anderer Bereiche.
      app_settings.dashboard_settings = JSON.stringify({
        ...dashboard_settings,
        assessment_monitor_visible,
        cultural_fit_visible,
        matching_visible
      })

      const input = { id: partner.id, app_settings }
      const onSuccess = () => {
        success(t(I18N_PREFIX + 'success'))
        setSubmitting(false)
      }

      mutate({ input }, { onSuccess })
    }
  })

  const toggle_appearance = 'with-left-label-and-description'

  return (
    <SettingsContainer
      title={t(I18N_PREFIX + 'title')}
      description={t(I18N_PREFIX + 'description')}
      onSubmit={formik.handleSubmit}
      dirty={formik.dirty}
      isLoading={formik.isSubmitting}
    >
      <SEO title={t('seo.titles.settings_dashboard_components')} />
      <div className='flex flex-col gap-y-4'>
        {[
          {
            label: t(I18N_PREFIX + 'assessment_monitor.title'),
            description: t(I18N_PREFIX + 'assessment_monitor.description'),
            enabled: formik.values.assessment_monitor_visible,
            setEnabled: () =>
              formik.setFieldValue(
                'assessment_monitor_visible',
                !formik.values.assessment_monitor_visible
              )
          },
          {
            label: t(I18N_PREFIX + 'cultural_fit.title'),
            description: t(I18N_PREFIX + 'cultural_fit.description'),
            enabled: formik.values.cultural_fit_visible,
            setEnabled: () =>
              formik.setFieldValue(
                'cultural_fit_visible',
                !formik.values.cultural_fit_visible
              )
          },
          {
            label: t(I18N_PREFIX + 'matching.title'),
            description: t(I18N_PREFIX + 'matching.description'),
            enabled: formik.values.matching_visible,
            setEnabled: () =>
              formik.setFieldValue(
                'matching_visible',
                !formik.values.matching_visible
              )
          }
        ].map((toggle, index) => (
          <Fragment key={index}>
            <Toggle {...{ ...toggle, appearance: toggle_appearance }} />
            {index < 2 && <Divider />}
          </Fragment>
        ))}
      </div>
    </SettingsContainer>
  )
}

export default DashboardComponents
